import { AfterViewInit, Component, DestroyRef, ElementRef, inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { copyrightLayer, leafletOptions, tooltipOptions, worldBounds, zoomOptions } from './map.config';
import { getAppInsights } from '../../config/app-insights/app-insights';
import { latLngBounds, Map, Marker } from 'leaflet';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ShipmentMapService } from './services/shipment-map.service';
import { catchError, EMPTY, NEVER, Subscription, switchMap, tap } from 'rxjs';
import { Coordinate, Coordinates } from './models/map';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WindwardService } from '../shipment-details/services/windward.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Shipment } from '../../pages/overview/models/shipment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-shipment-map',
  standalone: true,
  templateUrl: './shipment-map.component.html',
  imports: [LeafletModule, MatProgressSpinner],
  styleUrl: './shipment-map.component.scss'
})
export class ShipmentMapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('resizeContainer') resizeContainer!: ElementRef;

  @Input() set shipment(value: Shipment | null) {
    this.shipmentData = value;
    if (this.shipmentData) {
      if (this.gpsTrackingField()) {
        this.mapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.gpsTrackingField()!.toString());
      } else if (this.shipmentData.transportMode === 'Ocean') {
        this.handleWindwardMap();
      } else {
        this.getCoordinates().subscribe();
      }
    }
  }

  map: Map | null = null;
  isLoading = true;
  activeMarkers: Marker[] = [];
  mapUrl: SafeResourceUrl | null = null;
  readonly leafletOptions = leafletOptions;

  private destroyRef = inject(DestroyRef);
  private resizeObserverRef: ResizeObserver | null = null;
  private tileErrorReported = false;
  private shipmentData: Shipment | null = null;
  private mapService = inject(ShipmentMapService);
  private matSnackBar = inject(MatSnackBar);
  private windwardService = inject(WindwardService);
  private sanitizer = inject(DomSanitizer);

  // Reported once per component instance — tileerror fires for every failed tile,
  // and a broken basemap would otherwise flood telemetry.
  private readonly onTileError = () => {
    if (this.tileErrorReported) {
      return;
    }
    this.tileErrorReported = true;
    const error = new Error('Map basemap tiles failed to load.');
    getAppInsights()?.trackException({ exception: error });
    console.error(error);
  };

  ngAfterViewInit() {
    this.resizeObserverRef = new ResizeObserver(() => this.map?.invalidateSize());
    this.resizeObserverRef.observe(this.resizeContainer.nativeElement);
  }

  ngOnDestroy() {
    this.resizeObserverRef?.disconnect();
    copyrightLayer.off('tileerror', this.onTileError);
  }

  onMapReady(map: Map) {
    this.map = map;
    copyrightLayer.off('tileerror', this.onTileError).on('tileerror', this.onTileError);
    setTimeout(() => map.invalidateSize(true));
  }

  onZoomChange() {
    this.map?.invalidateSize();
  }

  private handleWindwardMap() {
    this.windwardService
      .getMapLinksFor(this.shipmentData!.shipmentNo)
      .pipe(
        switchMap(windward => {
          const containers = this.shipmentData?.containers ?? [];
          let mainMapUrl: string | undefined;

          if (containers.length > 0) {
            mainMapUrl = containers
              .map(
                ({ containerNo }) => windward.find(link => link.mainMap && link.containerNumber === containerNo)?.url
              )
              .find(Boolean);
          } else {
            mainMapUrl = windward.find(link => link.url)?.url;
          }

          if (mainMapUrl) {
            this.mapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(mainMapUrl);
            return EMPTY;
          }

          return this.getCoordinates();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
  private getCoordinates() {
    return this.mapService.getCoordinates(this.shipmentData!.shipmentNo).pipe(
      tap(coordinates => {
        this.isLoading = false;
        this.showTooltipsOnMap(coordinates);
      }),
      catchError(error => {
        console.error(error);
        this.isLoading = false;
        this.matSnackBar.open('Could not get coordinates. Please try again later.', 'x');
        return NEVER;
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private showTooltipsOnMap(coords: Coordinates) {
    if (coords) {
      this.createOriginTooltip(coords.origin);
      this.createDestinationTooltip(coords.destination);
      this.zoomIntoCoordinates();
    }
  }

  private createOriginTooltip(coords: Coordinate | null) {
    if (coords) {
      const marker = this.mapService
        .originMarker(coords)
        .bindTooltip(this.mapService.originTooltip(this.shipmentData!.routing), tooltipOptions.origin)
        .openTooltip()
        .addTo(this.map!);
      this.activeMarkers.push(marker);
    }
  }

  private createDestinationTooltip(coords: Coordinate | null) {
    if (coords) {
      const marker = this.mapService
        .destinationMarker(coords)
        .bindTooltip(this.mapService.destinationTooltip(this.shipmentData!.routing), tooltipOptions.destination)
        .openTooltip()
        .addTo(this.map!);
      this.activeMarkers.push(marker);
    }
  }

  private zoomIntoCoordinates() {
    switch (this.activeMarkers.length) {
      case 0:
        this.map!.flyToBounds(worldBounds);
        break;
      case 1:
        this.map!.flyTo(this.activeMarkers[0].getLatLng(), zoomOptions.max);
        break;
      case 2:
        const bounds = latLngBounds(this.activeMarkers.map(marker => marker.getLatLng()));
        this.map!.flyToBounds(bounds, { padding: [100, 0], maxZoom: zoomOptions.twoMarkersMax });
        break;
    }
    this.map!.invalidateSize();
  }

  private gpsTrackingField() {
    return this.shipmentData?.customFields['gpsTracking'];
  }
}
